import { Box, Grid, Paper, Skeleton, Stack } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import {
    SELF_ORDER_COL1_KEYS,
    SELF_ORDER_COL2_KEYS,
    SELF_ORDER_COL3_KEYS,
} from './self-order-common-setting-column-keys';
import './scss/self-order-common-setting.page.scss';

/**
 * 单条配置行骨架：上为标题条、下为表单项区域（与稿面「标签 + 开关/单选/输入」总高度接近）。
 */
function SelfOrderSettingRowSkeleton() {
    return (
        <Grid item xs={12} className="self-order-common-setting-page__setting-item">
            <Stack spacing={0.5}>
                <Skeleton
                    animation="wave"
                    variant="rounded"
                    width="60%"
                    height={20}
                    sx={{ borderRadius: '4px' }}
                />
                <Skeleton
                    animation="wave"
                    variant="rounded"
                    height={40}
                    width="100%"
                    sx={{ borderRadius: '8px' }}
                />
            </Stack>
        </Grid>
    );
}

/**
 * 单列内：分区标题骨架 + 配置行列表（行数与 {@link SELF_ORDER_COL*_KEYS} 一致）。
 * @param titleHint - 可访问性用区段名（不渲染真实区标题，避免与骨架不同步）
 * @param rowCount - 该列 `keys` 长度
 * @param columnIndex - 列下标，用于行 `key` 全局唯一
 */
function SelfOrderColumnPanelSkeleton({ titleHint, rowCount, columnIndex }: { titleHint: string; rowCount: number; columnIndex: number }) {
    return (
        <Grid item xs={12} md={4}>
            <Paper
                className="self-order-common-setting-page__panel"
                component="section"
                elevation={0}
                /** 列分区：与实页三列分区标题语义一致 */
                aria-label={titleHint}
            >
                {/* 与真实区标题 20/24 对齐 */}
                <Skeleton
                    className="self-order-common-setting-page__section-title-skeleton"
                    animation="wave"
                    variant="rounded"
                    height={24}
                    width="55%"
                    sx={{ display: 'block' }}
                />
                <Grid container spacing={2} sx={{ mt: 0.5 }} aria-hidden>
                    {Array.from({ length: rowCount }, (_, i) => (
                        <SelfOrderSettingRowSkeleton key={`self-order-skel-r-${columnIndex}-${i}`} />
                    ))}
                </Grid>
            </Paper>
        </Grid>
    );
}

/**
 * 云 RBP「自助点餐通用设置」首包数据未就绪时：三列白卡 + 与实表同序的行数，顶栏不占用本组件。
 */
export function SelfOrderCommonSettingPageSkeleton() {
    const sectionTitleHints = [
        i18n.t('self_order_setting_page_section_ordering_rules'),
        i18n.t('self_order_setting_page_section_menu_display'),
        i18n.t('self_order_setting_page_section_customer_ui'),
    ];
    const rowCounts = [SELF_ORDER_COL1_KEYS.length, SELF_ORDER_COL2_KEYS.length, SELF_ORDER_COL3_KEYS.length] as const;

    return (
        <Box
            className="self-order-common-setting-page self-order-common-setting-page--skeleton"
            /** 首包设置项拉取中，供读屏识别 */
            aria-label={`${i18n.t('self_order_setting_page_label')}, ${i18n.t('general_text_loading')}`}
            aria-busy="true"
        >
            <Box
                className="self-order-common-setting-page__surface"
                sx={{
                    overflow: 'visible',
                    borderRadius: '16px',
                    backgroundColor: 'var(--fill-neutral-100)',
                    boxShadow: '0 0 4px 0 var(--shadow-25)',
                    p: 2,
                }}
            >
                <Box className="self-order-common-setting-page__body">
                    <Grid container spacing={3} className="self-order-common-setting-page__columns">
                        {rowCounts.map((count, colIdx) => (
                            <SelfOrderColumnPanelSkeleton
                                key={`self-order-skel-col-${colIdx}`}
                                columnIndex={colIdx}
                                titleHint={sectionTitleHints[colIdx] ?? `column-${colIdx + 1}`}
                                rowCount={count}
                            />
                        ))}
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}
